import React from 'react';
import PropTypes from 'prop-types';
import TextField from 'Components/ComponentLibrary/Components/Uncategorized/TextField';
import StartAdornment from 'Components/ComponentLibrary/Components/Inputs/Search/SearchInput1/StartAdornment';
import {
  FDStack,
} from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MultipicklistNewSelectItem
  from 'Components/ComponentLibrary/Components/Inputs/Multipicklist/BigHierarchyMultipicklistControl/MultipicklistNewSelectItem';
import MultipicklistNewGroupHeader
  from 'Components/ComponentLibrary/Components/Inputs/Multipicklist/BigHierarchyMultipicklistControl/MultipicklistNewGroupHeader';

const useStyles = buildUseStyles('BigHierarchyMultipicklistNew', (theme => ({
  searchField: {
    '& .MuiInputBase-root.Mui-focused': {
      background: theme.palette.facet.lightGray5,
    },
  },
  inputRoot: {
    minHeight: '32px',
    color: theme.palette.facet.darkGray,
    background: theme.palette.facet.lightGray5,
    borderRadius: theme.radius.lg,
    '&:hover': {
      background: theme.palette.facet.lightGray5,
    },
    '& ::placeholder': {
      color: theme.palette.facet.darkGray,
    },
  },
  innerInput: {
    padding: theme.spacing(2),
  },
})));

function BigHierarchyMultipicklistNew({
  onChange,
  testName,
  value,
  searchLabel,
  iconMapper,
  handleSearchDebounced,
  groups,
  handleMultipleChange,
  hasSearch,
}) {
  const classes = useStyles();

  return (
    <FDStack direction={'column'} spacing={2}>
      { hasSearch ? (
        <TextField
          className={classes.searchField}
          placeholder={searchLabel}
          variant="outlined"
          onChange={handleSearchDebounced}
          test="hierarchy-search"
          size="small"
          InputProps={{
            classes: {
              root: classes.inputRoot,
              input: classes.innerInput,
            },
            startAdornment: (
              <StartAdornment />
            ),
          }}
        />
      ) : null}
      <FDStack direction={'column'} spacing={5}>
        {Object.entries(groups).map(([groupName, optionsInGroup]) => {
          const hasOptionsInGroup = optionsInGroup?.filter(({ matchesSearchTerm }) => matchesSearchTerm).length > 0;
          if (!hasOptionsInGroup) {
            return null;
          }
          const { description } = optionsInGroup[0];
          return (
            <FDStack direction={'column'} spacing={description ? 3 : 1}>
              <MultipicklistNewGroupHeader
                description={description}
                groupName={groupName}
                icon={iconMapper(groupName)}
              />
              <FDStack direction={'column'} spacing={1}>
                {optionsInGroup.map(({ resource: option, matchesSearchTerm }) => {
                  const isChecked = value.includes(option.id());
                  return matchesSearchTerm ? (
                    <MultipicklistNewSelectItem
                      key={option.attr('id')}
                      onChange={(checked) => {
                        return onChange(handleMultipleChange(option.id(), checked));
                      }}
                      isSelected={isChecked}
                      testName={testName}
                      itemId={option.attr('id')}
                      itemPrompt={option.attr('prompt')}
                    />
                  ) : null;
                })}
              </FDStack>
            </FDStack>
          );
        })}
      </FDStack>
    </FDStack>
  );
}

BigHierarchyMultipicklistNew.defaultProps = {
  searchLabel: 'Search',
  hasSearch: true,
};

BigHierarchyMultipicklistNew.propTypes = {
  onChange: PropTypes.func.isRequired,
  testName: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  searchLabel: PropTypes.string,
  iconMapper: PropTypes.func,
  handleSearchDebounced: PropTypes.func.isRequired,
  groups: PropTypes.object.isRequired,
  handleMultipleChange: PropTypes.func.isRequired,
  hasSearch: PropTypes.bool,
};

export default BigHierarchyMultipicklistNew;
